import React, { useState } from 'react';
import { browserHistory } from 'react-router';

// Constants
import { BusinessUnit, BusinessUnitComponent } from '@zola-helpers/client/dist/es/@types';
import { PLANNING_V2 } from 'components/onboard/onboard-v2/flows/constants';

// Types
import type {
  GenericOnboardingRequest,
  LocationAndGuestCountValues,
} from '@zola/zola-ui/src/components/OnboardV2/types';

// Components
import { OnboardingProvider } from '@zola/zola-ui/src/components/OnboardV2/context';
import OnboardContainer from '@zola/zola-ui/src/components/OnboardV2/OnboardContainer';
import OnboardSection from '@zola/zola-ui/src/components/OnboardV2/OnboardSection';
import GuestCountStep from '@zola/zola-ui/src/components/OnboardV2/common/steps/GuestCountStep';
import CoreAccountDetails from '@zola/zola-ui/src/components/OnboardV2/common/sections/CoreAccountDetails';
import FinishUpSection from 'components/onboard/onboard-v2/sections/FinishUpSection';
import ConditionalWeddingLocationStep from 'components/onboard/onboard-v2/ConditionalWeddingLocationStep';

// Util
import { formatLocationAndGuestCount } from '@zola/zola-ui/src/components/OnboardV2/util/submitFormatters';
import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import {
  trackOnboardingCompleted,
  trackOnboardingStarted,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';
import { onboardUser } from '@zola-helpers/client/dist/es/api/onboard';
import { useAppDispatch, useAppSelector } from 'reducers/index';
import { getUserContext, getUserIsGuest } from 'selectors/user/userSelectors';
import { getUserContext as getUserContextAction } from 'actions/UserActions';
import determineFlow from 'util/experiments/postOnboardMobileHelper';

// Styles
import { hidePortalOnMobile } from '@zola/zola-ui/src/components/OnboardV2/OnboardContainer/OnboardContainer.styles';

type OnboardingLocation = {
  city: string | undefined;
  state: string | undefined;
};

const WeddingPlanningOnboardFlowV2 = (): JSX.Element => {
  const userContext = useAppSelector(getUserContext);
  const isExistingUser = !useAppSelector(getUserIsGuest);
  const [onboardingLocation, setOnboardingLocation] = useState<OnboardingLocation>({
    city: undefined,
    state: undefined,
  });

  const { city, state } = onboardingLocation;
  const dispatch = useAppDispatch();

  const lastSectionStart = isExistingUser ? 2 : 3;
  return (
    <OnboardingProvider
      flowTrackingData={{
        flow_name: PLANNING_V2,
        flow_version: 2,
        flow_step_count: 6,
      }}
      flowTrackingContractsData={{
        business_unit: 'UNATTRIBUTED',
        business_category: 'UNATTRIBUTED',
        flow_type: 'ONBOARDING',
        flow_entry: 'GET_STARTED',
      }}
      userContext={userContext}
    >
      <OnboardContainer
        css={hidePortalOnMobile}
        onOnboardSuccessCb={() => {
          if (isMobile()) {
            return determineFlow({
              hasVenue: false, // we are not asking this anymore
              city,
              state,
            }).then(flowKey => {
              // make sure our userContext is up to date so we can display personalized data in the post onboarding flow
              dispatch(getUserContextAction()).catch(() => undefined);
              browserHistory.push(`/onboard/explore?flowKey=${flowKey}`);
            });
          }
          window.location.assign('/wedding/manage');
          return Promise.resolve();
        }}
        onClose={() => {
          window.location.assign('/');
        }}
        progressTitles={['Get started', 'The basics', 'Finish up']}
        progressOverrides={[0, 1, lastSectionStart]}
        businessUnit={BusinessUnit.WEDDINGS}
        businessUnitComponent={BusinessUnitComponent.WEDDING_PLANNING}
        onOnboardingStarted={() => {
          trackOnboardingStarted(
            BusinessUnit.WEDDINGS,
            BusinessUnitComponent.WEDDINGS,
            isExistingUser
          );
          trackOnboardingStarted(
            BusinessUnit.WEDDINGS,
            BusinessUnitComponent.WEDDING_PLANNING,
            isExistingUser
          );
        }}
      >
        <CoreAccountDetails
          appDispatch={dispatch}
          onAccountCreation={() => {
            trackOnboardingCompleted(BusinessUnit.WEDDINGS, BusinessUnitComponent.WEDDINGS, {
              isExistingUser,
            });
            trackOnboardingCompleted(
              BusinessUnit.WEDDINGS,
              BusinessUnitComponent.WEDDING_PLANNING,
              {
                isExistingUser,
              }
            );
          }}
        />

        <OnboardSection
          onFormSubmit={(values: GenericOnboardingRequest) => {
            const onboardingRequest = formatLocationAndGuestCount(
              (values as unknown) as LocationAndGuestCountValues,
              true
            );
            // if user skips both questions, no need for API call
            if (onboardingRequest) {
              setOnboardingLocation({
                city: onboardingRequest.wedding_details.city,
                state: onboardingRequest.wedding_details.state_province,
              });
              return onboardUser(onboardingRequest);
            }
            return Promise.resolve();
          }}
        >
          <ConditionalWeddingLocationStep />
          <GuestCountStep />
        </OnboardSection>
        <FinishUpSection
          onFormSubmit={values => {
            return onboardUser({
              ...values,
              existing_account: true,
            });
          }}
        />
      </OnboardContainer>
    </OnboardingProvider>
  );
};

export default WeddingPlanningOnboardFlowV2;
